export interface ContentBounds {
    /** Normalized (0–1) bounds of the visible product inside the source image. */
    x: number;
    y: number;
    width: number;
    height: number;
}

export const FULL_CONTENT_BOUNDS: ContentBounds = { x: 0, y: 0, width: 1, height: 1 };

const SAMPLE_MAX_SIDE = 280;
const ALPHA_THRESHOLD = 20;
const WHITE_THRESHOLD = 10;
const MIN_COVERAGE = 0.04;

const cache = new Map<string, ContentBounds>();

/**
 * Locate the product inside a mockup photo so artwork can be positioned against
 * the object itself instead of the surrounding transparent/white padding.
 * Returns full bounds when detection is not possible (tainted canvas, blank image).
 */
export function detectImageContentBounds(
    image: HTMLImageElement,
    cacheKey?: string
): ContentBounds {
    const key = cacheKey || image.currentSrc || image.src;
    if (key) {
        const cached = cache.get(key);
        if (cached) return cached;
    }

    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    if (!naturalWidth || !naturalHeight) return FULL_CONTENT_BOUNDS;

    try {
        const scale = Math.min(1, SAMPLE_MAX_SIDE / Math.max(naturalWidth, naturalHeight));
        const width = Math.max(1, Math.round(naturalWidth * scale));
        const height = Math.max(1, Math.round(naturalHeight * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return FULL_CONTENT_BOUNDS;

        ctx.drawImage(image, 0, 0, width, height);
        const { data } = ctx.getImageData(0, 0, width, height);

        let minX = width;
        let minY = height;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
                const index = (y * width + x) * 4;
                const alpha = data[index + 3];
                if (alpha <= ALPHA_THRESHOLD) continue;

                const r = data[index];
                const g = data[index + 1];
                const b = data[index + 2];
                // Skip near-white studio padding so white AND black garments both detect.
                const isNearWhite =
                  r >= 255 - WHITE_THRESHOLD &&
                  g >= 255 - WHITE_THRESHOLD &&
                  b >= 255 - WHITE_THRESHOLD;
                if (isNearWhite) continue;

                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
            }
        }

        if (maxX < minX || maxY < minY) return FULL_CONTENT_BOUNDS;

        const bounds: ContentBounds = {
            x: minX / width,
            y: minY / height,
            width: (maxX - minX + 1) / width,
            height: (maxY - minY + 1) / height,
        };

        if (bounds.width * bounds.height < MIN_COVERAGE) return FULL_CONTENT_BOUNDS;

        if (key) cache.set(key, bounds);
        return bounds;
    } catch {
        return FULL_CONTENT_BOUNDS;
    }
}
